import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import {
  // useDownloadPwtTransactionReport,
  useDownloadTransactionReport,
  useGetAllMerchant,
  useGetCardTransactionsCount,
  useGetMerchantTerminal,
  // useGetPwtTransactionCount,
} from "../misc/mutations";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import LineLoader from "@/components/composables/LineLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TransactionReportEmailModal from "../misc/transactionReportEmailModal";
import useControl from "@/hooks/useControl";

const formSchema = z.object({
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  merchantId: z.string().min(1, "Merchant ID is required"),
  terminalId: z.string().optional(),
});

const TransactionReport = () => {
  return (
    <div>
      <Tabs defaultValue="card">
        <TabsList>
          <TabsTrigger className="" value="card">
            Card Transactions
          </TabsTrigger>
          <TabsTrigger value="pwt">PWT Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="card">
          <CardTransactionReport />
        </TabsContent>
        {/* <TabsContent value="pwt">
          <PWTTransactionReport />
        </TabsContent> */}
      </Tabs>
    </div>
  );
};

export default TransactionReport;

const CardTransactionReport = () => {
  const form = useForm({
    resolver: zodResolver(formSchema),
  });
  const {
    state: emailIsOpen,
    setTrue: openEmail,
    setFalse: closeEmail,
  } = useControl();
  const { data: merchants, isLoading: merchantIsLoading } = useGetAllMerchant({
    size: 1000000,
    page: 1,
  });

  const { data: transactionCount } = useGetCardTransactionsCount();

  const {
    mutate: getTerminals,
    data: terminals,
    isPending: terminalsIsLoading,
  } = useGetMerchantTerminal({
    merchantId: form.watch("merchantId"),
  });

  const {
    refetch: download,
    isFetching: isDownloading,
    // data: report,
  } = useDownloadTransactionReport({
    filter: {
      fromDate: form.watch("fromDate"),
      toDate: form.watch("toDate"),
      merchantId: form.watch("merchantId"),
      terminalId: form.watch("terminalId"),
    },
    onSuccess: () => {
      form.reset();
    },
  });

  useEffect(() => {
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch("merchantId")]);

  const downloadCardTransactionReport = (data: any) => {
    if (transactionCount.count > 1000) {
      openEmail();
    } else {
      download(data);
    }
  };

  return (
    <div>
      <h1 className="text-2xl">Card Transaction Report</h1>
      {(terminalsIsLoading || merchantIsLoading || isDownloading) && (
        <LineLoader />
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(downloadCardTransactionReport)}
          className="mt-8 gap-4 flex items-center"
        >
          <FormField
            control={form.control}
            name="merchantId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Merchant Name</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={`${field.value}`}
                >
                  <FormControl>
                    <SelectTrigger className="w-52 px-4">
                      <SelectValue placeholder="Select Merchant" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {merchants?.merchants.map((v, key) => (
                      <SelectItem key={v._id} value={v.merchantId}>
                        {v.name ?? v.merchantName} - {v.merchantId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="terminalId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Terminal ID</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={`${field.value}`}
                >
                  <FormControl>
                    <SelectTrigger className="w-52 px-4">
                      <SelectValue placeholder="Select Terminal ID" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {terminals?.length === 0 ? (
                      <SelectItem disabled value={"No"}>
                        No Terminal
                      </SelectItem>
                    ) : (
                      terminals?.map((v: any, key: number) => (
                        <SelectItem key={key} value={v.terminalId}>
                          {v.terminalId}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fromDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="toDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            className="self-end place-items-end justify-self-end bg-primary-500 disabled:bg-primary-100"
            type="submit"
            disabled={isDownloading}
          >
            {isDownloading ? <Loader2 className="animate-spin" /> : "Download"}
          </Button>
        </form>
      </Form>
      {emailIsOpen && (
        <TransactionReportEmailModal
          onClose={closeEmail}
          isOpen={emailIsOpen}
          params={{
            merchantId: form.watch("merchantId"),
            terminalId: form.watch("terminalId"),
            fromDate: form.watch("fromDate"),
            toDate: form.watch("toDate"),
          }}
          type="card"
        />
      )}
    </div>
  );
};

// const PWTTransactionReport = () => {
//   const form = useForm({
//     resolver: zodResolver(formSchema),
//   });
//   const {
//     state: emailIsOpen,
//     setTrue: openEmail,
//     setFalse: closeEmail,
//   } = useControl();

//   const { data: transactionCount } = useGetPwtTransactionCount();

//   const { data: merchants, isLoading: merchantIsLoading } = useGetAllMerchant({
//     size: 1000000,
//     page: 1,
//   });
//   const {
//     mutate: getTerminals,
//     data: terminals,
//     isPending: terminalsIsLoading,
//   } = useGetMerchantTerminal({
//     merchantId: form.watch("merchantId"),
//   });

//   const { refetch: download, isLoading: isDownloading } =
//     useDownloadPwtTransactionReport({
//       filter: {
//         fromDate: form.watch("fromDate"),
//         toDate: form.watch("toDate"),
//         merchantId: form.watch("merchantId"),
//         terminalId: form.watch("terminalId"),
//       },
//       onSuccess: () => {
//         form.reset();
//       },
//     });

//   useEffect(() => {
//     getTerminals();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [form.watch("merchantId")]);

//   const downloadPwtTransactionReport = (data: any) => {
//     if (transactionCount.count > 1000) {
//       openEmail();
//     } else {
//       download(data);
//     }
//   };

//   return (
//     <div>
//       <h1 className="text-2xl">PWT Transaction Report</h1>
//       {(terminalsIsLoading || merchantIsLoading || isDownloading) && (
//         <LineLoader />
//       )}
//       <Form {...form}>
//         <form
//           onSubmit={form.handleSubmit(downloadPwtTransactionReport)}
//           className="mt-8 gap-4 flex items-center"
//         >
//           <FormField
//             control={form.control}
//             name="merchantId"
//             render={({ field }) => (
//               <FormItem>
//                 <FormLabel>Merchant Name</FormLabel>
//                 <Select
//                   onValueChange={field.onChange}
//                   defaultValue={`${field.value}`}
//                 >
//                   <FormControl>
//                     <SelectTrigger className="w-52 px-4">
//                       <SelectValue placeholder="Select Merchant" />
//                     </SelectTrigger>
//                   </FormControl>
//                   <SelectContent>
//                     {merchants?.merchants.map((v, key) => (
//                       <SelectItem key={v._id} value={v.merchantId}>
//                         {v.merchantName} - {v.merchantId}
//                       </SelectItem>
//                     ))}
//                   </SelectContent>
//                 </Select>
//                 <FormMessage />
//               </FormItem>
//             )}
//           />

//           <FormField
//             control={form.control}
//             name="terminalId"
//             render={({ field }) => (
//               <FormItem>
//                 <FormLabel>Terminal ID</FormLabel>
//                 <Select
//                   onValueChange={field.onChange}
//                   defaultValue={`${field.value}`}
//                 >
//                   <FormControl>
//                     <SelectTrigger className="w-52 px-4">
//                       <SelectValue placeholder="Select Terminal ID" />
//                     </SelectTrigger>
//                   </FormControl>
//                   <SelectContent>
//                     {terminals?.length === 0 ? (
//                       <SelectItem disabled value={"No"}>
//                         No Terminal
//                       </SelectItem>
//                     ) : (
//                       terminals?.map((v: any, key: number) => (
//                         <SelectItem key={key} value={v.terminalId}>
//                           {v.terminalId}
//                         </SelectItem>
//                       ))
//                     )}
//                   </SelectContent>
//                 </Select>
//                 <FormMessage />
//               </FormItem>
//             )}
//           />

//           <FormField
//             control={form.control}
//             name="fromDate"
//             render={({ field }) => (
//               <FormItem>
//                 <FormLabel>Start Date</FormLabel>
//                 <FormControl>
//                   <Input type="date" {...field} />
//                 </FormControl>
//                 <FormMessage />
//               </FormItem>
//             )}
//           />

//           <FormField
//             control={form.control}
//             name="toDate"
//             render={({ field }) => (
//               <FormItem>
//                 <FormLabel>End Date</FormLabel>
//                 <FormControl>
//                   <Input type="date" {...field} />
//                 </FormControl>
//                 <FormMessage />
//               </FormItem>
//             )}
//           />

//           <Button
//             className="self-end place-items-end justify-self-end bg-primary-500 disabled:bg-primary-100"
//             type="submit"
//             disabled={isDownloading}
//           >
//             {isDownloading ? <Loader2 className="animate-spin" /> : "Download"}
//           </Button>
//         </form>
//       </Form>
//       {emailIsOpen && (
//         <TransactionReportEmailModal
//           onClose={closeEmail}
//           isOpen={emailIsOpen}
//           params={{
//             merchantId: form.watch("merchantId"),
//             terminalId: form.watch("terminalId"),
//             fromDate: form.watch("fromDate"),
//             toDate: form.watch("toDate"),
//           }}
//           type="pwt"
//         />
//       )}
//     </div>
//   );
// };
